import React from 'react';
import { __ } from 'artsteps2-common';
import SearchIcon from '@material-ui/icons/Search';
import InputAdornment from '@material-ui/core/InputAdornment';
import Overlay from '../../../generic/Overlay';
import ArtifactUploader from '../../../artifacts/ArtifactUploader';
import ArtifactList from '../../../artifacts/lists/ArtifactList';
import ArtifactEditor from '../../../artifacts/ArtifactEditor';
import utils from '../../../../utils';
import { compose, withState, withDispatch, withLifecycle } from '../../../../enhancers';
import {
  API_STATUS,
  getApiResource,
  getApiStatus,
  getUIProperty,
  getAuthUser,
  getFormProperty,
} from '../../../../reducers';
import { apiGET, setFormProperty, setUIProperty, setUIData } from '../../../../actions';

import case1Image from '../../../../styles/images/cases/case1.jpg';
import case2Image from '../../../../styles/images/cases/case2.jpg';
import case3Image from '../../../../styles/images/cases/case3.jpg';
import case4Image from '../../../../styles/images/cases/case4.jpg';
import case5Image from '../../../../styles/images/cases/case5.jpg';
import { HorizontalItems, StyledTextField, Text } from '../../../../styles/GenericStyled';
import { StyledFolderIcon, StyledListIcon } from '../../../artifacts/lists/ArtifactsStyled';
import MyToolTip from '../../../generic/MyToolTip';

const ARTIFACT_TYPES = ['image', 'video', 'object', 'text'];
const DISPLAY_CASES = [
  { type: 1, image: case1Image },
  { type: 2, image: case2Image },
  { type: 3, image: case3Image },
  { type: 4, image: case4Image },
  { type: 5, image: case5Image },
];

export const ExhibitionArtifactPanelView = ({
  showInFolders = false,
  setShowInFolders,
  artifacts = [],
  currentTab = ARTIFACT_TYPES[0],
  ready = true,
  searchTerm = '',
  exhibitionId,
  placingCase,
  selectedCase,
  placingArtifact,
  selectedArtifact,
  editingArtifactId,
  editingArtifactType,
  onSearch = () => Promise.resolve(false),
  onPlaceDisplayCase = () => Promise.resolve(false),
  onStopPlacing = () => Promise.resolve(false),
  onTabChange = () => Promise.resolve(false),
  onEditorClose = () => Promise.resolve(false),
  onUpdateArtifact = () => Promise.resolve(false),
}) => {
  const renderCase = ({ type, image }) => (
    <div className="item" key={type}>
      <div className="artifact-wrapper">
        <div
          role="button"
          tabIndex={0}
          className={`artifact display-case
            ${placingCase === type ? 'placing' : ''}
            ${selectedCase === type ? 'selected' : ''}
            ${placingArtifact || (placingCase && placingCase !== type) ? 'disabled' : ''}
          `}
          onKeyPress={event =>
            event.keyCode === 13 &&
            (placingCase === type ? onStopPlacing() : onPlaceDisplayCase(type))
          }
          onClick={() => (placingCase === type ? onStopPlacing() : onPlaceDisplayCase(type))}
        >
          <img
            className="ui image tiny centered"
            src={image}
            alt={type}
            draggable={!placingArtifact && !placingCase}
            onDragEnd={({ dataTransfer: { dropEffect = 'none' } = {} }) =>
              onStopPlacing().then(
                () => dropEffect !== 'none' && placingCase !== type && onPlaceDisplayCase(type),
              )
            }
            data-position="top center"
          />
        </div>
      </div>
    </div>
  );

  const getArtifacts = type => {
    const searchText = searchTerm.toLowerCase();
    return artifacts.filter(
      a =>
        (!type || a.type === type) &&
        (!searchText ||
          a.title.toLowerCase().indexOf(searchText) > -1 ||
          (a.caption && a.caption.toLowerCase().indexOf(searchText) > -1) ||
          (a.information && a.information.toLowerCase().indexOf(searchText) > -1) ||
          (Array.isArray(a.files) &&
            a.files.some(
              b =>
                (b.name && b.name.toLowerCase().indexOf(searchText) > -1) ||
                (b.meta && b.meta.text && b.meta.text.toLowerCase().indexOf(searchText) > -1),
            )) ||
          (Array.isArray(a.covers) &&
            a.covers.some(c => c.name && c.name.toLowerCase().indexOf(searchText) > -1)) ||
          (a.audio &&
            a.audio.bin &&
            a.audio.bin.name &&
            a.audio.bin.name.toLowerCase().indexOf(searchText) > -1) ||
          (a.audio && a.audio.uri && a.audio.uri.toLowerCase().indexOf(searchText) > -1)),
    );
  };

  const renderArtifacts = () => (
    <div className="toolbar-tabs artifact-tabs">
      <div className="ui top attached tabular menu">
        {ARTIFACT_TYPES.map(type => (
          <button
            key={type}
            className={`item ${currentTab === type ? 'active' : ''}`}
            onClick={() => onTabChange(type)}
          >
            {__(`${type}s`)}
            &nbsp;
            <span className="text small">({getArtifacts(type).length})</span>
          </button>
        ))}
      </div>
      {ARTIFACT_TYPES.map(type => {
        const filteredArtifacts = getArtifacts(type);
        return (
          <div
            key={type}
            className={`ui bottom attached tab segment ${currentTab === type ? 'active' : ''}`}
          >
            <ArtifactUploader type={type}>
              {artifacts.length === 0 && <div className="help-text">{__('help_artifacts')}</div>}
              {artifacts.length > 0 && filteredArtifacts.length === 0 && (
                <div className="help-text">{__(`no_artifact_${type}s_found`)}</div>
              )}
              <ArtifactList
                showInFolders={showInFolders}
                artifacts={filteredArtifacts}
                controls
                ready={ready}
                exhibitionId={exhibitionId}
              />
            </ArtifactUploader>
          </div>
        );
      })}
      <Overlay fixed open={!!editingArtifactId} onClose={onEditorClose}>
        <ArtifactEditor
          onClose={onEditorClose}
          onSave={artifact => onUpdateArtifact(artifact).then(() => onEditorClose())}
          exhibitionId={exhibitionId}
          artifactId={editingArtifactId}
          type={editingArtifactType}
        />
      </Overlay>
    </div>
  );

  return (
    <div className="exhibition-artifact-editor">
      <div className="toolbar-section">
        <Text type="h7">
          {__('display_cases')} ({DISPLAY_CASES.length})
        </Text>
        <div className="upload-container">
          <div className="artifact-list-wrapper">
            <div className="artifact-list display-cases">
              <div className="ui horizontal list">{DISPLAY_CASES.map(renderCase)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="toolbar-section stackable">
        <HorizontalItems>
          <Text type="h7">
            {__('artifacts')} ({artifacts.length})
          </Text>
          <span style={{ marginLeft: 'auto' }}>
            <MyToolTip title="Show in list" placement="top">
              <StyledListIcon
                selected={+!showInFolders}
                fontSize="large"
                onClick={() => setShowInFolders(false)}
              />
            </MyToolTip>
            <MyToolTip title="Show in folders" placement="top">
              <StyledFolderIcon
                selected={+showInFolders}
                fontSize="large"
                onClick={() => setShowInFolders(true)}
              />
            </MyToolTip>
          </span>
        </HorizontalItems>
        <div>
          <HorizontalItems>
            <StyledTextField
              style={{ padding: '10px' }}
              fullWidth
              variant="outlined"
              type="text"
              placeholder={__('search')}
              onChange={event => onSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </HorizontalItems>
        </div>
        {renderArtifacts()}
      </div>
    </div>
  );
};

const createQuery = ({ _id: user = null } = {}) => ({ filter: { user } });

const mapState = (state, { exhibitionId }) => {
  const currentUser = getAuthUser(state);
  return {
    currentUser,
    placingCase: getUIProperty(state, `exhibitions/${exhibitionId}/placingCase`),
    placingArtifact: getUIProperty(state, `exhibitions/${exhibitionId}/placingArtifact`),
    editingArtifactId: getUIProperty(state, 'artifacts/editingArtifactId'),
    editingArtifactType: getUIProperty(state, 'artifacts/editingArtifactType'),
    currentTab: getUIProperty(state, `exhibitions/${exhibitionId}/artifactTab`),
    artifacts: Object.values(getApiResource(state, 'artifacts', createQuery(currentUser))),
    ready: getApiStatus(state, 'artifacts', createQuery(currentUser)) === API_STATUS.IDLE,
    searchTerm: getFormProperty(state, 'artifacts', 'search'),
    showInFolders: getUIProperty(state, 'showInFolders'),
  };
};

const mapDispatch = (dispatch, { exhibitionId, currentUser }) => ({
  setShowInFolders: value => dispatch(setUIProperty('showInFolders', value)),
  onFetchArtifacts: () => dispatch(apiGET('artifacts', createQuery(currentUser))),
  onPlaceDisplayCase: displayCase =>
    dispatch(
      setUIData(`exhibitions/${exhibitionId}`, {
        placingCase: displayCase,
        placingArtifact: undefined,
      }),
    ),
  onStopPlacing: () =>
    dispatch(
      setUIData(`exhibitions/${exhibitionId}`, {
        placingCase: undefined,
        placingArtifact: undefined,
      }),
    ),
  onTabChange: tab => dispatch(setUIProperty(`exhibitions/${exhibitionId}/artifactTab`, tab)),
  onSearch: searchTerm => dispatch(setFormProperty('artifacts', 'search', searchTerm)),
  onEditorClose: () => dispatch(setUIProperty('artifacts/editingArtifactId', undefined)),
  onUpdateArtifact: artifact =>
    utils.exhibition
      .exportArtifact(artifact)
      .then(a => dispatch(setUIProperty(`exhibitions/${exhibitionId}/updatingArtifact`, a))),
});

const lifecycleMap = {
  onDidMount: ({ onFetchArtifacts }) => onFetchArtifacts(),
};

const ExhibitionArtifactPanel = compose(
  withState(mapState),
  withDispatch(mapDispatch),
  withLifecycle(lifecycleMap),
)(ExhibitionArtifactPanelView);

export default ExhibitionArtifactPanel;
